'use client';

import { scrollToViewPromise } from '@cc-shopping/utils/scrollToViewPromise';
import { Box, List } from '@mui/material';
import { type MouseEvent, startTransition, useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Link } from './components/Link';
import { Title } from './components/Title';
import { useHeadingsObserver } from './hooks/useHeadingsObserver';
import { useTableOfContents } from './hooks/useTableOfContents';
import type { TableOfContentsHeadingProps } from './types';

type Props = {
  headings: TableOfContentsHeadingProps[];
  tocId: string;
};

export function Desktop({ headings, tocId }: Props) {
  const { current: linkRefs } = useRef<Record<string, HTMLAnchorElement>>({});

  const { activeId, setActiveId, fireNavigateToSectionPageAction } = useTableOfContents({
    headings,
  });

  const { pauseScrollTracking, reenableScrollTracking } = useHeadingsObserver({
    activeId,
    headings,
    setActiveId,
    tocId,
  });

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const href = event.currentTarget.href;
      const headingId = href.substring(href.indexOf('#') + 1);
      const headingContent = event.currentTarget.innerText;
      pauseScrollTracking();
      startTransition(() => {
        setActiveId(headingId);
      });
      fireNavigateToSectionPageAction({
        action_input: `${headings.findIndex(element => element.id === headingId)}`,
        action_location_url: window.location.href,
        activity_id: uuid(),
        additional_attributes: {
          toc_section: headingContent,
        },
      });
      await scrollToViewPromise(headingId, 'smooth', 30, true);
      reenableScrollTracking();
    },
    [JSON.stringify(headings)],
  );

  const handleRef = useCallback((ref: HTMLAnchorElement | null, headingId: string) => {
    if (ref) {
      linkRefs[headingId] = ref;
    }
  }, []);

  useEffect(() => {
    const targetElement = linkRefs[activeId || ''];
    if (targetElement) {
      targetElement.classList.add('selected');
    }
    return () => {
      targetElement && targetElement.classList.remove('selected');
    };
  }, [activeId]);

  return (
    <Box
      id={tocId}
      sx={{
        display: { xs: 'none', md: 'block' },
        width: '100%',
        overflowY: 'auto',
        position: 'sticky',
        top: 10,
      }}
      component="nav"
      aria-label="Table of contents"
    >
      <Box
        sx={{
          padding: 2,
          paddingLeft: 3,
          marginBottom: 2,
          borderBottom: '4px solid',
          borderColor: 'primary.main',
        }}
      >
        <Title id="toc-title-desktop" />
      </Box>
      <List component="ol" disablePadding aria-labelledby="toc-title-desktop">
        {headings.map(heading => (
          <Box component="li" key={heading.id} sx={{ padding: 0 }}>
            <Link
              isSelected={activeId === heading.id}
              headingId={heading.id}
              handleRef={handleRef}
              onClick={handleClick}
            >
              {heading.header}
            </Link>
          </Box>
        ))}
      </List>
    </Box>
  );
}
